import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Megaphone, Trash2, Loader, Plus, User } from "lucide-react";
import {
  lmsGetNotices,
  lmsDeleteNotice,
  lmsGetClasses,
  lmsGetCourses,
  lmsGetStudents,
} from "../api/lmsApi";
import AddNoticeModal from "../components/AddNoticeModal";
import StaffChat from "./StaffChat";
import "../lms.css";

// Combined Notice Board / Chat page for teacher and admin — same page,
// same table, same AddNoticeModal/StaffChat, differing only by `role`:
// a teacher only sees notices they sent, while an admin gets a school-wide
// oversight view (every notice any teacher or the admin has sent, with an
// extra "From" column identifying who sent each one) and can post as
// themselves too. There's no dedicated "current teacher" lookup elsewhere
// in this LMS area yet — teacher_id resolves the same way every other page
// resolves its own id from userData. The school admin is a single account
// per school, so its own id doubles as school_id.
const getIdentity = (role) => {
  try {
    const ud = JSON.parse(localStorage.getItem("userData") || "{}");
    const schoolId = ud.school_id || ud.admin_id || ud.id || null;
    const creatorId = role === "teacher" ? ud.teacher_id || ud.id || null : schoolId;
    return { schoolId, creatorId };
  } catch {
    return { schoolId: null, creatorId: null };
  }
};

export default function NoticeBoard({ role }) {
  const isAdmin = role === "admin";
  const { schoolId, creatorId } = getIdentity(role);

  const [notices, setNotices] = useState([]);
  const [classes, setClasses] = useState([]);
  const [courses, setCourses] = useState([]);
  const [students, setStudents] = useState([]);

  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (!schoolId) return;
    fetchNotices();
    fetchTargets();
  }, [schoolId]);

  const fetchNotices = async () => {
    setLoading(true);
    try {
      // Admin gets a school-wide oversight view (no created_by filter) —
      // teacher only sees notices they personally sent.
      const params = isAdmin
        ? { school_id: schoolId }
        : { created_by_type: "teacher", created_by_id: creatorId, school_id: schoolId };
      const res = await lmsGetNotices(params);
      setNotices(res.data?.data || []);
    } catch (err) {
      console.error("fetchNotices error:", err);
      toast.error("Failed to load notices");
    } finally {
      setLoading(false);
    }
  };

  const fetchTargets = async () => {
    try {
      const [classesRes, coursesRes, studentsRes] = await Promise.all([
        lmsGetClasses(),
        lmsGetCourses({ school_id: schoolId }),
        lmsGetStudents({ school_id: schoolId }),
      ]);
      setClasses(classesRes.data?.data || classesRes.data || []);
      setCourses(coursesRes.data?.data || coursesRes.data || []);
      setStudents(studentsRes.data?.data || studentsRes.data || []);
    } catch (err) {
      console.error("fetchTargets error:", err);
      toast.error("Failed to load classes/batches/students");
    }
  };

  const scopeListFor = (scope_type) =>
    scope_type === "class" ? classes : scope_type === "batch" ? courses : students;

  const labelFor = (scope_type, id, list) => {
    const match = list.find((x) => String(x.id) === String(id));
    if (!match) return `#${id}`;
    if (scope_type === "student") {
      return `${match.first_name || ""} ${match.last_name || ""}`.trim() || `#${id}`;
    }
    return match.name;
  };

  // Resolve a friendlier target label ("Class 8-A") than a bare scope_id,
  // using the class/batch/student lists already fetched for the compose form.
  // A common notice's class scope can be "all" or a comma-separated list of
  // class ids, and a direct notice's student scope can be a comma-separated
  // list of student ids (multiple picked in the compose form) — not just one.
  const getScopeLabel = (n) => {
    if (n.scope_type === "class" && n.scope_id === "all") return "All Classes";
    if (String(n.scope_id).includes(",")) {
      const count = String(n.scope_id).split(",").length;
      return n.scope_type === "student" ? `${count} students` : `${count} classes`;
    }
    return labelFor(n.scope_type, n.scope_id, scopeListFor(n.scope_type));
  };

  // Resolve the full name list for the tooltip on a multi-target label.
  const getScopeTitle = (n) => {
    if (n.scope_id === "all" || !String(n.scope_id).includes(",")) return undefined;
    const list = scopeListFor(n.scope_type);
    return String(n.scope_id)
      .split(",")
      .map((id) => labelFor(n.scope_type, id, list))
      .join(", ");
  };

  const handleDelete = (e, noticeId) => {
    e.stopPropagation();
    if (!window.confirm("Delete this notice?")) return;
    lmsDeleteNotice(noticeId)
      .then(() => {
        toast.success("Notice deleted");
        setNotices((prev) => prev.filter((n) => n.id !== noticeId));
      })
      .catch((err) => {
        console.error("deleteNotice error:", err);
        toast.error(err.response?.data?.message || "Failed to delete notice");
      });
  };

  const colCount = isAdmin ? 5 : 4;

  return (
    <div>
      <div className="std-page-header" style={{ marginBottom: "20px" }}>
        <h1 className="std-page-title" style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <Megaphone className="text-blue-600" size={26} />
          Notice Board / Chat
        </h1>
        <p className="std-page-subtitle">
          {isAdmin
            ? "Every notice sent across the school on the left, message any student individually on the right"
            : "Post announcements on the left, message individual students on the right"}
        </p>
      </div>

      {showModal && (
        <AddNoticeModal
          schoolId={schoolId}
          createdByType={role}
          createdById={creatorId}
          classes={classes}
          courses={courses}
          students={students}
          onClose={() => setShowModal(false)}
          onCreated={fetchNotices}
        />
      )}

      <div className="notice-chat-columns">
        <div>
          <div
            style={{ marginBottom: "12px", display: "flex", justifyContent: "space-between", alignItems: "center" }}
          >
            <h2 style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "16px", fontWeight: 700, margin: 0 }}>
              <Megaphone className="text-blue-600" size={18} />
              Notice Board
            </h2>
            <button
              type="button"
              className="std-btn-primary"
              onClick={() => setShowModal(true)}
              style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
            >
              <Plus size={14} /> Add Notice
            </button>
          </div>

          <div className="std-table-card" style={{ maxHeight: "65vh", overflowY: "auto" }}>
            <table className="std-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Date Posted</th>
                  {isAdmin && <th>From</th>}
                  <th>Target</th>
                  <th style={{ textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={colCount} style={{ textAlign: "center", padding: 40 }}>
                      <Loader size={24} className="ms2-spin" style={{ margin: "0 auto 8px" }} />
                      <div style={{ color: "#64748b", fontSize: 14 }}>Loading notices...</div>
                    </td>
                  </tr>
                ) : notices.length === 0 ? (
                  <tr>
                    <td colSpan={colCount} style={{ textAlign: "center", padding: "48px 20px" }}>
                      <div
                        style={{
                          width: 56,
                          height: 56,
                          borderRadius: "50%",
                          background: "#f1f5f9",
                          color: "#94a3b8",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          margin: "0 auto 12px",
                        }}
                      >
                        <Megaphone size={26} />
                      </div>
                      <div style={{ fontSize: 15, fontWeight: 600, color: "#334155" }}>No notices sent yet</div>
                      <div style={{ fontSize: 13, color: "#94a3b8", marginTop: 4 }}>
                        Click "Add Notice" to post one.
                      </div>
                    </td>
                  </tr>
                ) : (
                  notices.map((n) => (
                    <tr key={n.id}>
                      <td>
                        <div style={{ fontWeight: 600, color: "#0f172a", fontSize: 14 }}>{n.title}</div>
                      </td>
                      <td>
                        <span style={{ fontSize: 13, color: "#64748b" }}>
                          {new Date(n.created_at).toLocaleDateString()}
                        </span>
                      </td>
                      {isAdmin && (
                        <td>
                          <div
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "4px",
                              fontSize: 13,
                              color: "#475569",
                            }}
                          >
                            <User size={12} style={{ color: "#94a3b8" }} />
                            {n.created_by_name || (n.created_by_type === "admin" ? "Admin" : "Teacher")}
                            <span style={{ textTransform: "capitalize", color: "#94a3b8" }}>
                              ({n.created_by_type})
                            </span>
                          </div>
                        </td>
                      )}
                      <td>
                        <span style={{ fontSize: 13, color: "#475569", textTransform: "capitalize" }}>
                          {n.scope_type}:{" "}
                        </span>
                        <span title={getScopeTitle(n)} style={{ fontSize: 13, color: "#0f172a" }}>
                          {getScopeLabel(n)}
                        </span>
                      </td>
                      <td style={{ textAlign: "right" }}>
                        <button
                          type="button"
                          className="std-btn-cancel"
                          onClick={(e) => handleDelete(e, n.id)}
                          title="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div>
          <StaffChat staffType={role} />
        </div>
      </div>
    </div>
  );
}
